import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

export interface MembershipInfo {
  membershipId: string;
  role: 'TENANT_ADMIN' | 'USER';
  tenant: { id: string; name: string; slug: string; status: string };
}

export interface MeResponse {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isPlatformAdmin: boolean;
  memberships: MembershipInfo[];
}

interface TenantContextValue {
  tenantId?: string;
  membership?: MembershipInfo;
  me?: MeResponse;
  isAdmin: boolean;
  setMe: (me: MeResponse) => void;
  selectTenant: (tenantId: string) => void;
}

const TenantContext = createContext<TenantContextValue>({
  isAdmin: false,
  setMe: () => undefined,
  selectTenant: () => undefined,
});

const STORAGE_KEY = 'usc:selectedTenant';

export function TenantProvider({ children }: { children: ReactNode }) {
  const [me, setMeState] = useState<MeResponse>();
  const [tenantId, setTenantId] = useState<string | undefined>(
    () => localStorage.getItem(STORAGE_KEY) ?? undefined,
  );

  const setMe = useCallback((next: MeResponse) => {
    setMeState(next);
    setTenantId((current) => {
      const valid = next.memberships.some((m) => m.tenant.id === current);
      if (valid) return current;
      const first = next.memberships[0]?.tenant.id;
      if (first) localStorage.setItem(STORAGE_KEY, first);
      else localStorage.removeItem(STORAGE_KEY);
      return first;
    });
  }, []);

  const selectTenant = useCallback((next: string) => {
    localStorage.setItem(STORAGE_KEY, next);
    setTenantId(next);
  }, []);

  const value = useMemo<TenantContextValue>(() => {
    const membership = me?.memberships.find((m) => m.tenant.id === tenantId);
    return {
      tenantId,
      membership,
      me,
      isAdmin: membership?.role === 'TENANT_ADMIN',
      setMe,
      selectTenant,
    };
  }, [tenantId, me, setMe, selectTenant]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  return useContext(TenantContext);
}
